import React, { useCallback, useEffect, useMemo } from 'react';
import s from './s.module.less';
import WebinarBooking from 'components/WebinarBooking';
import { EWebinarScheduleType } from 'types/common';
import { Button, message } from 'antd';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { getBillingPortalLink } from 'api/subscription';

interface CombinedBannerProps {
    fetchData: () => void;
    webinarTitle?: string;
    webinarDes?: string;
    webinarLink?: string;
    webinarScheduleType?: EWebinarScheduleType;
    showPaymentTips?: boolean;
}

const CombinedBanner: React.FC<CombinedBannerProps> = ({
    fetchData,
    webinarTitle,
    webinarDes,
    webinarLink,
    webinarScheduleType,
    showPaymentTips = false,
}) => {
    const [showWebinar, setShowWebinar] = React.useState(false);
    const [bannerIndex, setBannerIndex] = React.useState(0);
    const [webinarClosedTypes, setWebinarClosedTypes] = React.useState<EWebinarScheduleType[]>([]);

    useEffect(() => {
        const _webinarClosedTypes = localStorage.getItem('webinarClosedTypes');
        if (_webinarClosedTypes) {
            const webinarClosedTypesArray = JSON.parse(_webinarClosedTypes);
            if (Array.isArray(webinarClosedTypesArray)) {
                setWebinarClosedTypes(webinarClosedTypesArray);
            }
        }
    }, [webinarScheduleType]);

    const handleUpgradePayment = useCallback(async () => {
        try {
            const result = await getBillingPortalLink();
            if (!result.error) {
                const link = result.data.data;
                if (link) {
                    window.open(link, '_blank');
                } else {
                    message.error('Customer billing portal is for paid user only.');
                }
            }
        } catch (e) {
            console.log(e);
        }
    }, []);

    const hasWebinar = webinarTitle && webinarDes && webinarLink;

    const bannerContent = useMemo(() => {
        const contents = [];
        if (showPaymentTips) {
            contents.push({
                title: ' Your recent payment was declined',
                desc: 'To avoid service interruption, please update your payment information immediately. Thank you for your quick attention.',
                btnText: 'Update payment',
                btnClick: handleUpgradePayment,
            });
        }
        //https://app.asana.com/1/1201608580743313/project/1203117087311346/task/1210475638082476?focus=true one time
        if (hasWebinar && webinarScheduleType && !webinarClosedTypes.includes(webinarScheduleType)) {
            contents.push({
                title: webinarTitle,
                desc: webinarDes,
                btnText: 'Schedule now',
                btnClick: () => setShowWebinar(true),
                isWebinar: true,
            });
        }
        return contents;
    }, [showPaymentTips, hasWebinar, webinarScheduleType, webinarClosedTypes, handleUpgradePayment, webinarTitle, webinarDes]);

    if (bannerContent.length === 0) {
        return null;
    }

    return (
        <div className={s.combinedWrap}>
            <div className={s.content}>
                <div className={s.leftSection}>
                    <div className={s.tipIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M8.25706 3.0976C9.02167 1.7383 10.9788 1.7383 11.7434 3.0976L17.3237 13.0181C18.0736 14.3514 17.1102 15.9987 15.5805 15.9987H4.4199C2.89025 15.9987 1.92682 14.3514 2.67675 13.0181L8.25706 3.0976ZM11.0001 12.9988C11.0001 13.5511 10.5524 13.9988 10.0001 13.9988C9.44784 13.9988 9.00012 13.5511 9.00012 12.9988C9.00012 12.4465 9.44784 11.9988 10.0001 11.9988C10.5524 11.9988 11.0001 12.4465 11.0001 12.9988ZM10.0001 4.99878C9.44784 4.99878 9.00012 5.44649 9.00012 5.99878V8.99878C9.00012 9.55106 9.44784 9.99878 10.0001 9.99878C10.5524 9.99878 11.0001 9.55106 11.0001 8.99878V5.99878C11.0001 5.44649 10.5524 4.99878 10.0001 4.99878Z" fill="#FBBF24" />
                        </svg>
                    </div>
                    <div className={s.text}>
                        <div className={s.webinarSection}>
                            <div className={s.tipTitle}>{bannerContent[bannerIndex]?.title}</div>
                            <div className={s.tipDesc}>{bannerContent[bannerIndex]?.desc}</div>
                        </div>
                        <Button className={s.button} onClick={bannerContent[bannerIndex]?.btnClick}>
                            {bannerContent[bannerIndex]?.btnText}
                        </Button>

                    </div>
                </div>
                {
                    bannerContent[bannerIndex]?.isWebinar && (
                        <div
                            className={s.closeBtn}
                            onClick={() => {
                                if (webinarScheduleType && !webinarClosedTypes.includes(webinarScheduleType)) {
                                    webinarClosedTypes.push(webinarScheduleType);
                                    setWebinarClosedTypes([...webinarClosedTypes]);
                                    setBannerIndex(0);
                                }
                                localStorage.setItem('webinarClosedTypes', JSON.stringify(webinarClosedTypes));
                            }}
                        />
                    )
                }
                {bannerContent.length > 1 ?
                    <div className={s.rightSection}>
                        <span className="mr-3">{`${bannerIndex + 1} / ${bannerContent.length}`}</span>
                        {bannerIndex < bannerContent.length - 1
                            ? <span onClick={() => setBannerIndex((index) => index + 1)}> <RightOutlined /> </span>
                            : <span onClick={() => setBannerIndex((index) => index - 1)}><LeftOutlined /></span>}
                    </div> : null}
            </div>

            {hasWebinar && (
                <WebinarBooking
                    show={showWebinar}
                    link={webinarLink}
                    webinarScheduleType={webinarScheduleType}
                    onClose={(isSuccess) => {
                        if (isSuccess) {
                            fetchData?.();
                        }
                        setShowWebinar(false);
                    }}
                />
            )}
        </div>
    );
};

export default CombinedBanner;
